import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { ImageUp } from "lucide-react";
import { BenefitLogo } from "./AdminBenefits";

export const CATEGORIES = ["Alimentação", "Saúde", "Beleza", "Educação", "Serviços", "Moda", "Casa", "Lazer", "Automotivo", "Outros"];
const EMPTY = { name: "", category: "", address: "", description: "", how_to_use: "", original_price: "", price: "", active: true };
const num = (v) => (v === "" || v == null ? 0 : Number(String(v).replace(",", ".")));
const str = (v) => (v ? String(v).replace(".", ",") : "");

const Field = ({ label, children }) => <div className="space-y-1"><Label>{label}</Label>{children}</div>;

export const PartnerForm = ({ editing, onClose, onSaved, base, admin = false }) => {
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!editing) return;
    const src = editing.id ? { ...editing, ...(editing.pending_changes || {}) } : {};
    setForm({ ...EMPTY, ...src, original_price: str(src.original_price), price: str(src.price), how_to_use: src.how_to_use || "",
              address: src.address || "", category: src.category === "Outros" && !editing.id ? "" : (src.category || "") });
    setFile(null);
  }, [editing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const op = num(form.original_price);
  const p = num(form.price);
  const priceError = op && p && p > op ? "O preço com desconto não pode ser maior que o original" : "";
  const valid = form.name.trim().length >= 2 && form.category.trim().length >= 2 && form.description.trim().length >= 2 && !priceError;

  const save = async () => {
    setSaving(true);
    try {
      const body = { name: form.name, category: form.category.trim(), address: form.address, description: form.description,
                     how_to_use: form.how_to_use, original_price: op || 0, price: p || 0, ...(admin ? { active: form.active } : {}) };
      const { data } = editing.id ? await api.patch(`${base}/${editing.id}`, body) : await api.post(base, body);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`${base}/${data.id}/logo`, fd);
      }
      toast.success(admin ? "Salvo" : "Enviado para aprovação do administrador");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="partner-form-dialog" className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Editar produto / empresa" : "Novo produto / empresa"}</DialogTitle>
          {!admin && <DialogDescription>Cadastros e edições só aparecem na vitrine depois da aprovação do administrador.</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Nome da empresa ou produto"><Input value={form.name} onChange={set("name")} data-testid="partner-name-input" /></Field>
          <Field label="Categoria">
            <Input list="partner-categories" value={form.category} onChange={set("category")} placeholder="Ex.: Alimentação" data-testid="partner-category-input" />
            <datalist id="partner-categories">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Endereço completo"><Input value={form.address} onChange={set("address")} placeholder="Rua, número, bairro, cidade - UF" data-testid="partner-address-input" /></Field>
          <Field label="Descrição curta"><Textarea rows={3} value={form.description} onChange={set("description")} data-testid="partner-description-input" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preço original (R$)"><Input inputMode="decimal" value={form.original_price} onChange={set("original_price")} placeholder="100,00" data-testid="partner-original-price-input" /></Field>
            <Field label="Preço para associado (R$)"><Input inputMode="decimal" value={form.price} onChange={set("price")} placeholder="60,00" data-testid="partner-price-input" /></Field>
          </div>
          {priceError && <p className="text-xs text-rose-500" data-testid="partner-price-error">{priceError}</p>}
          <Field label="Como usar (opcional)"><Textarea rows={2} value={form.how_to_use} onChange={set("how_to_use")} data-testid="partner-howto-input" /></Field>
          <Field label="Foto (PNG, JPG ou WEBP até 2 MB)">
            <div className="flex items-center gap-3">
              <BenefitLogo url={editing?.pending_logo_url || editing?.logo_url} name={form.name} />
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                     onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="partner-photo-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="partner-photo-btn">
                <ImageUp className="h-4 w-4 mr-2" /> {file ? file.name.slice(0, 24) : "Escolher foto"}
              </Button>
            </div>
          </Field>
          {admin && (
            <div className="flex items-center justify-between pt-1">
              <Label>Exibir na vitrine</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="partner-active-switch" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className="gold-btn" disabled={saving || !valid} onClick={save} data-testid="partner-save-btn">
            {saving ? "Salvando..." : admin ? "Salvar" : "Enviar para aprovação"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
